import React, { useState, useEffect } from 'react';
import { XMarkIcon, ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { planningAPI } from '../../services/api';
import { useNotifications } from '../notifications/NotificationProvider';

const QuickScheduleModal = ({ show, onClose, session, crews = [], onSuccess }) => {
  const { showError, showSuccess } = useNotifications();
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedCrews, setSelectedCrews] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasExperimentalCrews, setHasExperimentalCrews] = useState(false);

  // Calculate end time based on start time and session duration
  const calculateEndTime = (startTime, duration) => {
    if (!startTime || !duration) return '';

    const [hours, minutes] = startTime.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);

    const endDate = new Date(startDate.getTime() + duration * 60000);
    return endDate.toTimeString().slice(0, 5);
  };

  const endTime = calculateEndTime(selectedTime, session?.duration);
  const sessionDuration = session?.duration || 0;

  // Check if session has experimental crews
  useEffect(() => {
    if (session && session.crew_assignments) {
      const experimentalCrews = session.crew_assignments.filter((assignment) => {
        const crew = crews.find((c) => c.id === parseInt(assignment.crew_id));
        return crew && crew.is_experimental;
      });
      setHasExperimentalCrews(experimentalCrews.length > 0);
    }
  }, [session, crews]);

  // Reset form when modal opens
  useEffect(() => {
    if (show && session) {
      setSelectedTime('');
      setSelectedCrews([]);
      setIsSubmitting(false);
    }
  }, [show, session]);

  const handleCrewToggle = (crewId) => {
    setSelectedCrews((prev) => {
      if (prev.includes(crewId)) {
        return prev.filter((id) => id !== crewId);
      } else {
        return [...prev, crewId];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedTime) {
      showError('Please select a time for the session');
      return;
    }

    if (selectedCrews.length === 0) {
      showError('Please select at least one crew to schedule');
      return;
    }

    setIsSubmitting(true);

    try {
      const scheduleData = {
        crew_ids: selectedCrews,
        start_time: selectedTime,
        date: session.date ? session.date.split('T')[0] : null,
        coaches: [], // No coaches for quick scheduling
        lineups: {}, // Empty lineups - will use default crew lineups
      };

      const result = await planningAPI.scheduleSessionCrews(session.id, scheduleData);

      showSuccess(result.message || 'Session scheduled successfully');
      onSuccess(result);
      onClose();
    } catch (error) {
      console.error('Error scheduling session:', error);
      showError('Failed to schedule session. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!show || !session) return null;

  // If session has experimental crews, show warning instead
  if (hasExperimentalCrews) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Quick Scheduling Not Available</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="mb-6 flex items-start space-x-3 rounded-lg bg-amber-50 p-4">
            <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 text-amber-400" />
            <div>
              <h4 className="text-sm font-medium text-amber-800">Experimental Crews Detected</h4>
              <p className="mt-1 text-sm text-amber-700">
                This session contains experimental crews which require detailed lineup
                configuration. Please use the Planner or Autoplanner for scheduling these sessions.
              </p>
            </div>
          </div>

          <div className="flex flex-col space-y-3 sm:flex-row sm:justify-end sm:space-x-3 sm:space-y-0">
            <button
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
            <button
              onClick={() => {
                onClose();
                // Navigate to planner with session date and crews
                const searchParams = new URLSearchParams();
                if (session.date) {
                  searchParams.set('date', session.date.split('T')[0]);
                }
                const crewIds = session.crew_assignments?.map((a) => a.crew_id) || [];
                if (crewIds.length > 0) {
                  searchParams.set('crews', crewIds.join(','));
                }
                window.location.href = `/planner?${searchParams.toString()}`;
              }}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Open Planner
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Quick Schedule Session</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-900">{session.title}</h4>
          <p className="text-sm text-gray-500">{session.description}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Session Duration Info */}
          <div className="rounded-lg bg-gray-50 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-gray-700">Session Duration:</span>
              <span className="text-gray-900">{sessionDuration} minutes</span>
            </div>
          </div>

          {/* Time Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Start Time</label>
            <div className="mt-1 flex items-center space-x-2">
              <ClockIcon className="h-5 w-5 text-gray-400" />
              <input
                type="time"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          {/* End Time Display */}
          {selectedTime && endTime && (
            <div className="rounded-lg bg-blue-50 p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-blue-700">End Time:</span>
                <span className="text-blue-900">{endTime}</span>
              </div>
            </div>
          )}

          {/* Crew Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Select Crews to Schedule
            </label>
            <div className="mt-2 max-h-48 space-y-2 overflow-y-auto">
              {session.crew_assignments?.map((assignment) => {
                const crew = crews.find((c) => c.id === parseInt(assignment.crew_id));
                if (!crew) return null;

                return (
                  <label key={crew.id} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={selectedCrews.includes(crew.id)}
                      onChange={() => handleCrewToggle(crew.id)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900">{crew.name}</span>
                      <span className="ml-2 text-xs text-gray-500">({crew.boat_type})</span>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col space-y-3 pt-4 sm:flex-row sm:justify-end sm:space-x-3 sm:space-y-0">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || selectedCrews.length === 0 || !selectedTime}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {isSubmitting ? 'Scheduling...' : 'Schedule Session'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickScheduleModal;
